import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area,
} from "recharts";
import {
  Flame, Trophy, Target, Dumbbell, TrendingUp, Calendar,
  Plus, ChevronLeft, Zap, Award, Star, Lock, BarChart2,
  CheckCircle, Scale, Activity,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── Helpers ──────────────────────────────────────────────────────────────────
function authHeaders() {
  const token = localStorage.getItem("fitsmith_token") || "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}
function fmtDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}
function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, gold = false, animate = false }) {
  return (
    <div className={`rounded-2xl border p-5 flex flex-col gap-1 transition-all duration-300
      ${gold ? "bg-[#C9A84C]/8 border-[#C9A84C]/30" : "bg-[#111] border-white/6"}`}>
      <div className={`mb-1 ${gold ? "text-[#C9A84C]" : "text-zinc-500"}`}>{icon}</div>
      <div className={`text-3xl font-black tracking-tight ${gold ? "text-[#C9A84C]" : "text-white"} ${animate ? "animate-pulse" : ""}`}>
        {value}
      </div>
      <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      {sub && <div className="text-xs text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm shadow-2xl">
      <p className="text-zinc-400 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-bold" style={{ color: p.color }}>
          {p.value} {p.name === "weight_kg" ? "kg gewicht" : p.name === "fat_pct" ? "% vet" : "kg"}
        </p>
      ))}
    </div>
  );
}

// Consistency calendar — 13 weken × 7 dagen
function ConsistencyCalendar({ calendar }) {
  const dayNames = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
  // Groepeer per week (ma–zo)
  const weeks = [];
  for (let i = 0; i < calendar.length; i += 7) {
    weeks.push(calendar.slice(i, i + 7));
  }
  const trained = calendar.filter(d => d.trained).length;
  const pct = calendar.length ? Math.round((trained / calendar.length) * 100) : 0;

  return (
    <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold">Trainingsconsistentie</h3>
          <p className="text-zinc-500 text-xs mt-0.5">Laatste 90 dagen</p>
        </div>
        <div className="text-right">
          <p className="text-[#C9A84C] font-black text-2xl">{pct}%</p>
          <p className="text-zinc-600 text-xs">{trained} / {calendar.length} dagen</p>
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1">
            {week.map((day, di) => (
              <div
                key={di}
                title={`${day.date}${day.trained ? " — Getraind ✓" : ""}`}
                className={`w-3.5 h-3.5 rounded-sm transition-colors cursor-default
                  ${day.trained
                    ? "bg-[#C9A84C]"
                    : new Date(day.date) > new Date() ? "bg-transparent" : "bg-white/5"
                  }`}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
          <div className="w-2.5 h-2.5 rounded-sm bg-[#C9A84C]" /> Getraind
        </div>
        <div className="flex items-center gap-1.5 text-xs text-zinc-500">
          <div className="w-2.5 h-2.5 rounded-sm bg-white/5" /> Rust
        </div>
      </div>
    </div>
  );
}

// Badges grid
function BadgeGrid({ badges }) {
  return (
    <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <Award size={16} className="text-[#C9A84C]" />
        <h3 className="text-white font-bold">Achievements</h3>
        <span className="ml-auto text-xs text-zinc-500">
          {badges.filter(b => b.unlocked).length}/{badges.length} behaald
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {badges.map(b => (
          <div key={b.id}
            className={`rounded-xl border p-4 text-center transition-all duration-300
              ${b.unlocked
                ? "bg-[#C9A84C]/8 border-[#C9A84C]/30 shadow-[0_0_20px_rgba(201,168,76,0.1)]"
                : "bg-white/2 border-white/5 opacity-40 grayscale"}`}>
            <div className={`text-3xl mb-2 ${b.unlocked ? "" : "filter grayscale"}`}>
              {b.unlocked ? b.icon : "🔒"}
            </div>
            <p className={`text-xs font-bold ${b.unlocked ? "text-[#C9A84C]" : "text-zinc-500"}`}>
              {b.naam}
            </p>
            <p className="text-zinc-600 text-xs mt-0.5 leading-tight">{b.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// PR Board
function PRBoard({ prs }) {
  const top = prs.slice(0, 9);
  return (
    <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <Trophy size={16} className="text-[#C9A84C]" />
        <h3 className="text-white font-bold">Persoonlijke Records</h3>
      </div>
      {top.length === 0 ? (
        <p className="text-zinc-600 text-sm italic">Log trainingen met gewichten om je PR's te zien.</p>
      ) : (
        <div className="space-y-2">
          {top.map((pr, i) => (
            <div key={pr.exercise}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border
                ${i === 0 ? "bg-[#C9A84C]/8 border-[#C9A84C]/25" : "bg-white/2 border-white/5"}`}>
              <span className={`text-sm font-black w-6 text-center ${i < 3 ? "text-[#C9A84C]" : "text-zinc-600"}`}>
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{pr.exercise}</p>
                <p className="text-zinc-600 text-xs">{fmtDate(pr.date)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className={`font-black text-lg ${i === 0 ? "text-[#C9A84C]" : "text-white"}`}>
                  {pr.max_kg} kg
                </p>
                {pr.reps > 0 && <p className="text-zinc-600 text-xs">{pr.reps} reps</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function ProgressPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState("");
  const [measureForm, setMeasureForm] = useState({ weight_kg: "", fat_pct: "" });
  const [savingMeasure, setSavingMeasure] = useState(false);
  const [measureSaved, setMeasureSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("overzicht"); // overzicht | kracht | lichaam | records

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, measRes] = await Promise.all([
        fetch(`${API}/api/progress-dashboard`, { headers: authHeaders() }),
        fetch(`${API}/api/body-measurements`, { headers: authHeaders() }),
      ]);
      const dashData = await dashRes.json();
      const measData = await measRes.json();
      setData(dashData);
      setMeasurements(measData.measurements || []);
      if (dashData.strength_progress) {
        const keys = Object.keys(dashData.strength_progress);
        if (keys.length > 0) setSelectedExercise(keys[0]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveMeasurement = async () => {
    if (!measureForm.weight_kg && !measureForm.fat_pct) return;
    setSavingMeasure(true);
    try {
      await fetch(`${API}/api/body-measurements`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          weight_kg: measureForm.weight_kg ? parseFloat(measureForm.weight_kg) : null,
          fat_pct: measureForm.fat_pct ? parseFloat(measureForm.fat_pct) : null,
        }),
      });
      setMeasureForm({ weight_kg: "", fat_pct: "" });
      setMeasureSaved(true);
      setTimeout(() => setMeasureSaved(false), 2500);
      load();
    } catch (e) {}
    setSavingMeasure(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">Voortgang laden...</p>
      </div>
    </div>
  );

  const exerciseData = selectedExercise && data?.strength_progress?.[selectedExercise]
    ? data.strength_progress[selectedExercise].map(p => ({ ...p, date: fmtDateShort(p.date) }))
    : [];

  const lastWeight = measurements.length > 0 ? measurements[measurements.length - 1]?.weight_kg : null;
  const motivatie = data?.streak >= 7 ? "Sterker dan vorige week 🔥"
    : data?.streak >= 3 ? "Je bent op een roll! Blijf gaan 💪"
    : data?.this_week >= 2 ? "Lekker bezig deze week!"
    : "Consistency beats motivation";

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <div className="bg-[#0a0a0a] border-b border-white/5 px-4 sm:px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate("/account")}
          className="text-zinc-500 hover:text-white transition-colors">
          <ChevronLeft size={20} />
        </button>
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">FitSmith</p>
          <h1 className="text-white font-bold text-lg leading-none">Mijn Voortgang</h1>
        </div>
        {data?.streak > 0 && (
          <div className="ml-auto flex items-center gap-2 bg-[#C9A84C]/10 border border-[#C9A84C]/25 rounded-full px-4 py-1.5">
            <Flame size={14} className="text-[#C9A84C]" />
            <span className="text-[#C9A84C] font-black text-sm">{data.streak} dagen streak</span>
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">

        {/* Motivatie banner */}
        <div className="mb-6 px-5 py-3 bg-[#C9A84C]/6 border border-[#C9A84C]/20 rounded-xl">
          <p className="text-[#C9A84C] text-sm font-semibold italic">"{motivatie}"</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={<Flame size={20} />}
            label="Dagstreak"
            value={data?.streak ?? 0}
            sub={data?.max_streak > 0 ? `Record: ${data.max_streak}d` : "Begin vandaag!"}
            gold={data?.streak > 0}
          />
          <StatCard
            icon={<Target size={20} />}
            label="Deze week"
            value={`${data?.this_week ?? 0}x`}
            sub="trainingen voltooid"
          />
          <StatCard
            icon={<Dumbbell size={20} />}
            label="Totaal"
            value={data?.total_sessions ?? 0}
            sub="sessies afgerond"
          />
          <StatCard
            icon={<Activity size={20} />}
            label="Laatste training"
            value={data?.last_training ? fmtDate(data.last_training) : "–"}
            sub={lastWeight ? `${lastWeight} kg lichaamsgewicht` : ""}
          />
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 mb-6 bg-[#111] border border-white/6 rounded-xl p-1 overflow-x-auto">
          {[
            { id: "overzicht", label: "Consistentie", icon: <Calendar size={13} /> },
            { id: "kracht", label: "Kracht", icon: <TrendingUp size={13} /> },
            { id: "lichaam", label: "Lichaam", icon: <Scale size={13} /> },
            { id: "records", label: "Records & Badges", icon: <Trophy size={13} /> },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`progress-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all
                ${activeTab === t.id
                  ? "bg-[#C9A84C] text-black"
                  : "text-zinc-500 hover:text-white"}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── CONSISTENTIE TAB ─────────────────────────────────────────────────── */}
        {activeTab === "overzicht" && (
          <div className="space-y-4">
            <ConsistencyCalendar calendar={data?.calendar || []} />

            {/* Weekdoelen widget */}
            <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={16} className="text-[#C9A84C]" />
                <h3 className="text-white font-bold">Weekvoortgang</h3>
              </div>
              {[2, 3, 4].map(goal => {
                const done = Math.min(data?.this_week ?? 0, goal);
                const pct = Math.round((done / goal) * 100);
                return (
                  <div key={goal} className="mb-4 last:mb-0">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-zinc-400 font-semibold">{goal}x per week doel</span>
                      <span className={done >= goal ? "text-[#C9A84C] font-bold" : "text-zinc-500"}>
                        {done}/{goal} {done >= goal ? "✓ Gehaald!" : ""}
                      </span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: done >= goal ? GOLD : "#3f3f46" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Streak card */}
            <div className={`rounded-2xl border p-6 ${data?.streak >= 3 ? "bg-[#C9A84C]/6 border-[#C9A84C]/25" : "bg-[#111] border-white/6"}`}>
              <div className="flex items-center gap-4">
                <div className={`text-5xl font-black ${data?.streak >= 3 ? "text-[#C9A84C]" : "text-zinc-600"}`}>
                  🔥 {data?.streak ?? 0}
                </div>
                <div>
                  <p className="text-white font-bold">Dagen op rij getraind</p>
                  <p className="text-zinc-500 text-sm">Persoonlijk record: {data?.max_streak ?? 0} dagen</p>
                  {data?.streak === 0 && (
                    <p className="text-zinc-600 text-xs mt-1">Train vandaag om je streak te starten!</p>
                  )}
                  {data?.streak >= 7 && (
                    <p className="text-[#C9A84C] text-xs mt-1 font-semibold">Don't break the chain! 💪</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── KRACHT TAB ───────────────────────────────────────────────────────── */}
        {activeTab === "kracht" && (
          <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#C9A84C]" />
                <h3 className="text-white font-bold">Kracht Progressie</h3>
              </div>
              {Object.keys(data?.strength_progress || {}).length > 0 ? (
                <select
                  value={selectedExercise}
                  onChange={e => setSelectedExercise(e.target.value)}
                  data-testid="exercise-select"
                  className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-300 focus:outline-none focus:border-[#C9A84C]/40 max-w-xs">
                  {Object.keys(data.strength_progress).map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              ) : null}
            </div>

            {exerciseData.length < 2 ? (
              <div className="py-16 text-center">
                <BarChart2 size={40} className="text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">Log minimaal 2 trainingen met gewichten<br />om je progressiegrafiek te zien.</p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={exerciseData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                    <defs>
                      <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={GOLD} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#52525b", fontSize: 11 }} axisLine={false} tickLine={false} unit="kg" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="max_kg" stroke={GOLD} strokeWidth={2.5}
                      fill="url(#goldGrad)" dot={{ fill: GOLD, r: 3 }} activeDot={{ r: 5, fill: GOLD }} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="mt-4 flex items-center gap-6">
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-widest">Start</p>
                    <p className="text-white font-bold">{exerciseData[0]?.max_kg} kg</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-widest">Nu</p>
                    <p className="text-[#C9A84C] font-black text-lg">{exerciseData[exerciseData.length - 1]?.max_kg} kg</p>
                  </div>
                  {exerciseData.length >= 2 && (
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-widest">Progressie</p>
                      <p className={`font-bold ${exerciseData[exerciseData.length - 1]?.max_kg > exerciseData[0]?.max_kg ? "text-emerald-400" : "text-zinc-400"}`}>
                        {exerciseData[exerciseData.length - 1]?.max_kg > exerciseData[0]?.max_kg
                          ? `+${(exerciseData[exerciseData.length - 1]?.max_kg - exerciseData[0]?.max_kg).toFixed(1)} kg`
                          : "–"}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── LICHAAM TAB ──────────────────────────────────────────────────────── */}
        {activeTab === "lichaam" && (
          <div className="space-y-4">
            {/* Invoer formulier */}
            <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <Plus size={16} className="text-[#C9A84C]" />
                <h3 className="text-white font-bold">Meting toevoegen</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-2">
                    Gewicht (kg)
                  </label>
                  <input type="number" step="0.1" value={measureForm.weight_kg}
                    onChange={e => setMeasureForm(f => ({ ...f, weight_kg: e.target.value }))}
                    placeholder="bijv. 82.5"
                    data-testid="weight-input"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40" />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-2">
                    Vetpercentage (%) <span className="text-zinc-700 normal-case">optioneel</span>
                  </label>
                  <input type="number" step="0.1" value={measureForm.fat_pct}
                    onChange={e => setMeasureForm(f => ({ ...f, fat_pct: e.target.value }))}
                    placeholder="bijv. 18.5"
                    data-testid="fat-input"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40" />
                </div>
              </div>
              <button onClick={saveMeasurement} disabled={savingMeasure || (!measureForm.weight_kg && !measureForm.fat_pct)}
                data-testid="save-measurement-btn"
                className="w-full py-3 bg-[#C9A84C] text-black font-bold text-sm uppercase tracking-widest rounded-xl disabled:opacity-40 transition-opacity flex items-center justify-center gap-2">
                {measureSaved ? <><CheckCircle size={15} /> Opgeslagen!</> : savingMeasure ? "Opslaan..." : <><Plus size={15} /> Meting opslaan</>}
              </button>
            </div>

            {/* Grafiek */}
            <div className="bg-[#111] border border-white/6 rounded-2xl p-6">
              <h3 className="text-white font-bold mb-5">Lichaamsprogressie</h3>
              {measurements.length < 2 ? (
                <div className="py-12 text-center">
                  <Scale size={36} className="text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">Voeg 2 of meer metingen toe<br />om je progressie te zien.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={measurements.map(m => ({ ...m, date: fmtDateShort(m.date) }))}
                    margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#52525b", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="weight_kg" stroke={GOLD} strokeWidth={2.5}
                      dot={{ fill: GOLD, r: 3 }} activeDot={{ r: 5 }} name="weight_kg" />
                    <Line type="monotone" dataKey="fat_pct" stroke="#60a5fa" strokeWidth={2}
                      dot={{ fill: "#60a5fa", r: 3 }} activeDot={{ r: 5 }} name="fat_pct" strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              )}

              {/* Metingen lijst */}
              {measurements.length > 0 && (
                <div className="mt-4 space-y-1.5 max-h-48 overflow-y-auto">
                  {[...measurements].reverse().slice(0, 10).map((m, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/2 text-xs">
                      <span className="text-zinc-400">{fmtDate(m.date)}</span>
                      {m.weight_kg && <span className="text-white font-semibold">{m.weight_kg} kg</span>}
                      {m.fat_pct && <span className="text-blue-400">{m.fat_pct}% vet</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── RECORDS & BADGES TAB ─────────────────────────────────────────────── */}
        {activeTab === "records" && (
          <div className="space-y-4">
            <PRBoard prs={data?.prs || []} />
            <BadgeGrid badges={data?.badges || []} />
          </div>
        )}
      </div>
    </div>
  );
}
